import type {
  MediaRecorderTranscriptFinalTurn,
  MediaRecorderTranscriptPartialTurn,
  MediaRecorderTranscriptTurn,
  MediaTranscriberEngine,
  MediaTranscriberEventHandlers,
} from "@/store/audio/recording/media-types";
import type {
  DesktopRecorderTranscriptFinalTurn,
  DesktopRecorderTranscriptPartialTurn,
  DesktopRecorderTranscriptTurn,
} from "@/store/audio/recording/media-transcriber/transcriber.types";
import { AudioRecordingErrorStage } from "@/domains/errors";

export class DesktopMediaTranscriber implements MediaTranscriberEngine {
  private handlers: MediaTranscriberEventHandlers = {};
  private readonly emitError: (error: unknown, stage?: AudioRecordingErrorStage) => void;

  constructor({
    emitError,
  }: {
    emitError: (error: unknown, stage?: AudioRecordingErrorStage) => void;
  }) {
    this.emitError = emitError;
  }

  setEventHandlers(handlers: MediaTranscriberEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  async start(): Promise<void> {
    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    this.handlers.onStop?.();
  }

  async pause(): Promise<void> {
    // Desktop transcripts are fed in via the `desktop-media-recorder`.
    // Pausing happens there instead.
  }

  async resume(): Promise<void> {
    // Desktop transcripts are fed in via the `desktop-media-recorder`.
    // Resuming happens there instead.
  }

  dispose(): void {
    void this.stop();
  }

  handleTranscriptPartial({
    channel,
    partialTurn,
  }: {
    channel: 0 | 1;
    partialTurn: DesktopRecorderTranscriptPartialTurn;
  }): void {
    this.emitTranscript({
      channel,
      sourceTurn: partialTurn,
      kind: "partial",
    });
  }

  handleTranscriptFinal({
    channel,
    finalTurn,
  }: {
    channel: 0 | 1;
    finalTurn: DesktopRecorderTranscriptFinalTurn;
  }): void {
    this.emitTranscript({
      channel,
      sourceTurn: finalTurn,
      kind: "final",
    });
  }

  private emitTranscript({
    channel,
    sourceTurn,
    kind,
  }: {
    channel: 0 | 1;
    sourceTurn: DesktopRecorderTranscriptTurn;
    kind: "partial" | "final";
  }): void {
    try {
      const mapped = this.mapTranscriptTurn({ channel, turn: sourceTurn });
      if (kind === "partial") {
        this.handlers.onTranscriptPartial?.({
          partialTurn: mapped as MediaRecorderTranscriptPartialTurn,
        });
        return;
      }

      this.handlers.onTranscriptFinal?.({
        finalTurn: mapped as MediaRecorderTranscriptFinalTurn,
      });
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.TranscriptMap);
    }
  }

  private mapTranscriptTurn({
    channel,
    turn,
  }: {
    channel: 0 | 1;
    turn: DesktopRecorderTranscriptTurn;
  }): MediaRecorderTranscriptTurn {
    return {
      channel,
      transcript: turn.transcript,
      timestampMs: turn.timestampMs,
      isEndOfTurn: turn.isEndOfTurn,
      participantId: turn.participantId ?? null,
      turnOrder: turn.turnOrder,
      words: (turn.words ?? []).map(word => ({
        word: word.text,
        startMs: word.startMs,
        endMs: word.endMs,
      })),
      raw: turn.raw ?? turn,
    };
  }
}
